<template>
  <div>
    <v-dialog v-model="dialog" max-width="500" :persistent="loading" scrollable>
      <v-card>
        <v-card-title>
          {{ changelog.id ? "Editar" : "Novo" }} Changelog</v-card-title
        >
        <v-card-text>
          <v-form v-model="valid" @submit.prevent>
            <v-text-field
              label="Título"
              v-model="changelog.title"
              outlined
              required
              :rules="[(v) => !!v || 'Título é obrigatório']"
              :disabled="loading"
              class="mt-1"
            ></v-text-field>
            <v-textarea
              label="Conteúdo"
              v-model="changelog.content"
              outlined
              required
              dense
              rows="6"
              :rules="[(v) => !!v || 'Conteúdo é obrigatório']"
              :disabled="loading"
            />

            <v-alert
              @click="aiRewrite"
              :color="changelog.content && changelog.title ? 'primary' : 'grey'"
              dense
              text
              :style="{
                cursor:
                  changelog.content && changelog.title ? 'pointer' : 'default',
              }"
            >
              <v-icon
                left
                :color="
                  changelog.content && changelog.title ? 'primary' : 'grey'
                "
              >
                mdi-robot
              </v-icon>
              Reescrever com IA
            </v-alert>

            <v-text-field
              v-model="changelog.youtubeUrl"
              label="Link do Youtube"
              dense
              outlined
              prefix="youtube.com/watch?v="
              :disabled="loading"
            />
            <v-card
              outlined
              v-if="changelog.imageUrl"
              class="d-flex align-center gap-2 mb-4"
            >
              <div style="max-width: 50%">
                <v-img :src="changelog.imageUrl" height="100" contain></v-img>
              </div>
              <v-btn text @click="changelog.imageUrl = null">
                <v-icon>mdi-delete</v-icon>
              </v-btn>
            </v-card>

            <v-file-input
              v-else
              label="Imagem"
              v-model="image"
              outlined
              dense
            ></v-file-input>

            <v-select
              label="Categoria"
              v-model="changelog.category"
              :items="categories"
              outlined
              dense
              required
              :disabled="loading"
            >
              <template v-slot:item="{ item }">
                <v-icon left small :color="item.icon">{{ item.icon }}</v-icon>
                {{ item.text }}
              </template>
            </v-select>

            <v-text-field
              label="Data de Publicação"
              v-model="changelog.publishedAt"
              type="datetime-local"
              outlined
              dense
              clearable
              :disabled="loading"
            />

            <v-btn-toggle
              v-model="changelog.scope"
              class="d-flex"
              color="primary"
              :disabled="loading"
            >
              <v-btn
                v-for="scope in scopes"
                :key="scope.value"
                :value="scope.value"
                small
                class="flex-1"
              >
                <v-icon
                  left
                  small
                  :color="
                    changelog.scope === scope.value ? 'primary' : undefined
                  "
                >
                  {{ scope.icon }}
                </v-icon>
                {{ scope.text }}
              </v-btn>
            </v-btn-toggle>

            <v-btn-toggle
              v-model="changelog.size"
              class="d-flex mt-4"
              color="primary"
              :disabled="loading"
            >
              <v-btn
                v-for="size in sizes"
                :key="size.value"
                :value="size.value"
                small
                class="flex-1"
              >
                <v-icon
                  :color="changelog.size === size.value ? 'primary' : undefined"
                  small
                  left
                >
                  {{ size.icon }}
                </v-icon>
                {{ size.text }}
              </v-btn>
            </v-btn-toggle>
            <v-alert v-if="error" type="error" class="mt-4">
              {{ error }}
            </v-alert>
          </v-form>
        </v-card-text>
        <v-card-actions>
          <v-btn text @click="close" :disabled="loading">Cancelar</v-btn>
          <v-spacer></v-spacer>
          <v-btn
            color="primary"
            @click="save"
            :loading="loading"
            :disabled="!valid"
          >
            Salvar
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
    <ChangelogAiRewrite ref="aiRewriteModal" />
  </div>
</template>

<script>
import moment from "moment";
import CHANGELOG from "@/services/ultimate/changelogs.js";
import ChangelogAiRewrite from "./ChangelogAiRewrite.vue";

export default {
  components: {
    ChangelogAiRewrite,
  },
  data() {
    return {
      dialog: false,
      valid: false,
      loading: false,
      error: null,
      image: null,
      changelog: this.defaultChangelog(),
      scopes: [
        { text: "Geral", value: "GERAL", icon: "mdi-home" },
        { text: "Técnico", value: "TECHNICAL", icon: "mdi-account-wrench" },
        { text: "Admin", value: "ADMIN", icon: "mdi-crown" },
      ],
      sizes: [
        { text: "Pequena", value: "SMALL", icon: "mdi-circle-small" },
        { text: "Média", value: "MEDIUM", icon: "mdi-circle-medium" },
        { text: "Grande", value: "LARGE", icon: "mdi-circle" },
      ],
      categories: [
        { text: "Nova Funcionalidade", value: "NEW_FEATURE", icon: "mdi-plus" },
        { text: "Desempenho", value: "PERFORMANCE", icon: "mdi-speedometer" },
        { text: "Segurança", value: "SECURITY", icon: "mdi-shield-check" },
        { text: "Correção de Bug", value: "BUG_FIX", icon: "mdi-bug" },
        { text: "Nova API", value: "NEW_API", icon: "mdi-api" },
        { text: "Novo Relatório", value: "NEW_REPORT", icon: "mdi-chart-box" },
        { text: "Nova UI", value: "NEW_UI", icon: "mdi-palette" },
        { text: "Novo Tool", value: "NEW_TOOL", icon: "mdi-wrench" },
        { text: "Outro", value: "OTHER", icon: "mdi-information" },
      ],
    };
  },
  methods: {
    defaultChangelog() {
      return {
        title: "",
        content: "",
        youtubeUrl: "",
        imageUrl: "",
        publishedAt: moment().format("YYYY-MM-DDTHH:mm"),
        scope: "GERAL",
        category: "OTHER",
        size: "SMALL",
      };
    },
    async save() {
      try {
        this.loading = true;
        this.error = null;
        const publishedAt = this.changelog.publishedAt
          ? moment(this.changelog.publishedAt).toISOString()
          : null;

        const method = this.changelog.id
          ? CHANGELOG.updateChangelog
          : CHANGELOG.createChangelog;

        const { changelog } = await method({ ...this.changelog, publishedAt });

        if (this.image) {
          await CHANGELOG.updateChangelogImage(changelog.id, this.image);
        }

        this.loading = false;
        this.close();
        this.$emit("success");
      } catch (error) {
        this.error = error.message || "Erro ao criar changelog";
      } finally {
        this.loading = false;
      }
    },
    open(changelog = null) {
      this.dialog = true;
      this.changelog = Object.assign(
        {},
        this.defaultChangelog(),
        changelog || {}
      );

      this.image = null;
      if (changelog?.publishedAt) {
        this.changelog.publishedAt = moment(changelog.publishedAt).format(
          "YYYY-MM-DDTHH:mm"
        );
      }
    },
    close() {
      this.dialog = false;
    },
    async aiRewrite() {
      try {
        if (!this.changelog.content || !this.changelog.title) return;

        const response = await this.$refs.aiRewriteModal.open({
          title: this.changelog.title,
          content: this.changelog.content,
        });

        if (response) {
          this.changelog.title = response.title;
          this.changelog.content = response.content;
          this.changelog.category = response.category;
        }
      } catch (error) {
        this.error = error.message || "Erro ao reescrever com IA";
      } finally {
        this.loadingAiRewrite = false;
      }
    },
  },
  watch: {
    "multimedia.youtubeId": {
      handler(value) {
        if (!value) return;
        const regex =
          /(?:https?:\/\/)?(?:(?:(?:www\.?)?youtube\.com(?:\/(?:(?:watch\?.*?(v=[^&\s]+).*)|(?:v(\/.*))))?)|(?:youtu\.be(\/.*)?))/;
        const match = value.match(regex);
        if (match) {
          const id = match[1] || match[2];
          this.multimedia.youtubeId = id.replace("v=", "");
        }
      },
      immediate: true,
    },
  },
  mounted() {
    this.$root.$on("changelog-modal", this.open);
  },
};
</script>
